import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, ArrowRight, Mail, Phone, MapPin, Instagram, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILD_TYPES = [
  "Offset Smoker",
  "Vertical Cabinet",
  "Firepit / Firecraft",
  "Trailer Rig",
  "Custom / Other",
];

export default function Inquiry() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    build_type: BUILD_TYPES[0],
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received. We'll be in touch soon.");
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        build_type: BUILD_TYPES[0],
        budget: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not send inquiry. Try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="inquiry"
      className="relative bg-[#0A0A0A] border-t border-zinc-900 py-24 md:py-32 scroll-mt-20"
      data-testid="inquiry-section"
    >
      {/* Alias anchor so legacy #contact deep-links land here too */}
      <span id="contact" className="absolute -top-20" aria-hidden="true" />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0E0F11",
            color: "#F4F4F5",
            border: "1px solid #27272A",
            borderRadius: 0,
          },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left intro + contact */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-body text-[10px] tracking-[0.45em] uppercase text-[#FF7A00]">
                /04 — Contact Us
              </span>
              <div className="h-px w-12 bg-zinc-800" />
            </div>
            <h2
              className="font-heading uppercase tracking-tight leading-[0.9] text-5xl md:text-6xl lg:text-7xl steel-text"
              data-testid="inquiry-heading"
            >
              Commission
              <br />
              A One-Of-One.
            </h2>
            <p className="mt-8 text-zinc-400 text-base md:text-lg leading-relaxed max-w-lg">
              Give us a shout with what you're looking for and we will respond
              within 48 hours with a mock up and price estimate.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-zinc-900 pt-6">
              <div>
                <div className="font-body text-[10px] tracking-[0.4em] uppercase text-zinc-500">
                  Lead Time
                </div>
                <div className="mt-2 font-heading text-3xl text-zinc-100">
                  8–16 Weeks
                </div>
              </div>
              <div>
                <div className="font-body text-[10px] tracking-[0.4em] uppercase text-zinc-500">
                  Deposit
                </div>
                <div className="mt-2 font-heading text-3xl text-zinc-100">
                  30%
                </div>
              </div>
            </div>

            {/* Direct contact card */}
            <div className="mt-10 border border-zinc-900 bg-[#0E0F11] p-6 md:p-8" data-testid="contact-card">
              <div className="font-heading uppercase tracking-[0.3em] text-sm text-zinc-300 mb-5">
                Or Reach Out Direct
              </div>
              <ul className="space-y-4 text-zinc-300 font-body text-sm">
                <li className="flex items-center gap-3" data-testid="contact-email">
                  <Mail size={16} className="text-[#D9501E] shrink-0" />
                  <a href="mailto:emberlinefabrication@gmail.com" className="hover:text-[#FF7A00] transition-colors">
                    emberlinefabrication@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-3" data-testid="contact-phone">
                  <Phone size={16} className="text-[#D9501E] shrink-0" />
                  <a href="tel:+13214059910" className="hover:text-[#FF7A00] transition-colors">
                    (321) 405-9910
                  </a>
                </li>
                <li className="flex items-center gap-3" data-testid="contact-instagram">
                  <Instagram size={16} className="text-[#D9501E] shrink-0" />
                  <a
                    href="https://instagram.com/emberlinefabrication"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-[#FF7A00] transition-colors"
                  >
                    @emberlinefabrication
                  </a>
                </li>
                <li className="flex items-center gap-3" data-testid="contact-location">
                  <MapPin size={16} className="text-[#D9501E] shrink-0" />
                  <span>Cocoa, Florida · Visits by appointment</span>
                </li>
                <li className="flex items-center gap-3" data-testid="contact-hours">
                  <Clock size={16} className="text-[#D9501E] shrink-0" />
                  <span>Mon–Fri 08:00–18:00 · Sat by appt.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-7 border border-zinc-900 bg-[#0E0F11] p-6 md:p-10"
            data-testid="inquiry-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Name" htmlFor="name">
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="inquiry-input-name"
                  className={inputCls}
                  placeholder="Your name"
                />
              </Field>
              <Field label="Email" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  data-testid="inquiry-input-email"
                  className={inputCls}
                  placeholder="you@domain.com"
                />
              </Field>
              <Field label="Phone (optional)" htmlFor="phone">
                <input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="inquiry-input-phone"
                  className={inputCls}
                  placeholder="+1 555 000 0000"
                />
              </Field>
              <Field label="Build Type" htmlFor="build_type">
                <select
                  id="build_type"
                  value={form.build_type}
                  onChange={update("build_type")}
                  data-testid="inquiry-select-build-type"
                  className={inputCls}
                >
                  {BUILD_TYPES.map((b) => (
                    <option key={b} value={b} className="bg-[#0E0F11]">
                      {b}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Budget Range" htmlFor="budget" className="md:col-span-2">
                <input
                  id="budget"
                  type="text"
                  value={form.budget}
                  onChange={update("budget")}
                  data-testid="inquiry-input-budget"
                  className={inputCls}
                  placeholder="e.g. $8,000 – $15,000 or open to a quote"
                />
              </Field>
              <Field label="Tell Us About The Build" htmlFor="message" className="md:col-span-2">
                <textarea
                  id="message"
                  rows={6}
                  required
                  value={form.message}
                  onChange={update("message")}
                  data-testid="inquiry-input-message"
                  className={`${inputCls} resize-none`}
                  placeholder="Capacity, intended use, materials, aesthetic references…"
                />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap border-t border-zinc-900 pt-6">
              <p className="text-zinc-500 text-xs font-body uppercase tracking-[0.3em] max-w-xs">
                {submitted
                  ? "Inquiry filed. Watch your inbox."
                  : "We reply within 48 hours."}
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="inquiry-submit"
                className="btn-ember disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-[#0A0A0A] border border-zinc-800 px-4 py-4 text-zinc-100 placeholder:text-zinc-600 font-body text-base rounded-none focus:outline-none focus:border-[#D9501E] focus:ring-1 focus:ring-[#D9501E] transition-colors";

function Field({ label, htmlFor, className = "", children }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="font-body text-[10px] tracking-[0.4em] uppercase text-zinc-500"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
